/**
 * Wishlist Store — Stato globale wishlist condiviso tra header, bottom-nav, course-card, wishlist page.
 *
 * Uso nei template:
 *   $store.wishlist.count          — numero corsi salvati
 *   $store.wishlist.toggle(id)     — aggiunge/rimuove un corso
 *   $store.wishlist.has(id)        — controlla se un corso è in wishlist
 */
export function registerWishlistStore(Alpine) {
    Alpine.store('wishlist', {
        // Stato
        count: 0,
        items: [],
        loading: false,
        _fetched: false,
        _fetching: false,
        _ids: {},  // { courseId: true } — lookup veloce O(1)

        init() {
            // Conteggio iniziale da SSR (cookie)
            this.count = parseInt(window.IKOS?.wishlistCount, 10) || 0;

            // Dopo login/register con redirect: ri-fetcha per sincronizzare badge
            if (sessionStorage.getItem('ik_wishlist_just_logged')) {
                sessionStorage.removeItem('ik_wishlist_just_logged');
                this.refresh();
            }
        },

        /**
         * Controlla se un corso è in wishlist.
         * @param {number} courseId
         * @returns {boolean}
         */
        has(courseId) {
            return !!this._ids[courseId];
        },

        /**
         * Aggiunge o rimuove un corso dalla wishlist (optimistic UI).
         * @param {number} courseId
         * @returns {Promise<boolean>} — true se aggiunto, false se rimosso
         */
        async toggle(courseId) {
            const wasInWishlist = this.has(courseId);
            const adding = !wasInWishlist;

            // Optimistic update
            if (adding) {
                this._ids[courseId] = true;
                this.count++;
            } else {
                delete this._ids[courseId];
                this.count = Math.max(0, this.count - 1);
                // Rimuovi da items se caricati (pagina wishlist)
                this.items = this.items.filter(item => item.id !== courseId);
            }
            this._syncCookie();

            try {
                await this._apiFetch(adding ? 'POST' : 'DELETE', { courses_id: courseId });
            } catch (e) {
                // Rollback
                if (adding) {
                    delete this._ids[courseId];
                    this.count = Math.max(0, this.count - 1);
                } else {
                    this._ids[courseId] = true;
                    this.count++;
                }
                this._syncCookie();
                console.error('wishlist.toggle:', e);
            }

            return adding;
        },

        /**
         * Carica la wishlist completa dall'API (per la pagina wishlist).
         */
        async fetchWishlist() {
            if (this._fetching) return;
            this._fetching = true;
            this.loading = true;

            try {
                const data = await this._apiFetch('GET');
                const courses = data?._embedded?.courses || [];
                this.items = courses;
                this.count = courses.length;

                // Ricostruisci lookup IDs
                this._ids = {};
                for (const course of courses) {
                    this._ids[course.id] = true;
                }

                this._syncCookie();
                this._fetched = true;
            } catch (e) {
                console.error('wishlist.fetchWishlist:', e);
            } finally {
                this.loading = false;
                this._fetching = false;
            }
        },

        /**
         * Resetta lo stato e ri-fetcha la wishlist dall'API.
         * Da chiamare dopo cambio stato auth (login/register inline).
         */
        async refresh() {
            this._fetched = false;
            this._ids = {};
            await this.fetchWishlist();
        },

        /**
         * Svuota tutta la wishlist.
         */
        async clear() {
            const prevItems = [...this.items];
            const prevIds = Object.assign({}, this._ids);
            const prevCount = this.count;

            // Optimistic: svuota _ids chiave per chiave (reattività proxy Alpine)
            for (const key of Object.keys(this._ids)) {
                delete this._ids[key];
            }
            this.items = [];
            this.count = 0;
            this._syncCookie();

            try {
                await this._apiFetch('DELETE', { empty: true });
            } catch (e) {
                // Rollback
                Object.assign(this._ids, prevIds);
                this.items = prevItems;
                this.count = prevCount;
                this._syncCookie();
                console.error('wishlist.clear:', e);
                throw e;
            }
        },

        /**
         * Invia la wishlist per email.
         * @param {string} email
         */
        async sendEmail(email) {
            const data = await this._apiFetch('PATCH', { email });
            return data;
        },

        /**
         * Registra un ID corso come presente in wishlist (da SSR in_wishlist).
         * Chiamato dal wishlist-manager per sincronizzare lo stato iniziale.
         * @param {number} courseId
         */
        trackId(courseId) {
            this._ids[courseId] = true;
        },

        // ── Helpers privati ──────────────────────────────────

        /**
         * Sincronizza conteggio in cookie per SSR badge.
         */
        _syncCookie() {
            document.cookie = 'ik_wishlist_count=' + this.count + ';path=/;max-age=2592000;SameSite=Lax';
        },

        /**
         * Fetch wrapper per le API wishlist.
         * Usa Auth-Token per utenti loggati (stessa logica di _cartFetch).
         */
        async _apiFetch(method, body) {
            const apiBase = window.IKOS?.apiBase || '';
            const url = `${apiBase}/v1/wishlist`;

            const headers = {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            };

            // Auth token per utenti loggati
            const authToken = window.IKOS?.authToken;
            if (authToken) {
                headers['Auth-Token'] = authToken;
            }

            // Guest ID: condivide l'identità guest con SSR (stessa sessione PHP)
            const guestId = window.IKOS?.guestId;
            if (guestId) {
                headers['X-Guest-Id'] = guestId;
            }

            const options = { method, headers };

            if (body && method !== 'GET') {
                options.body = JSON.stringify(body);
            }

            let res;
            try {
                res = await fetch(url, options);
            } catch (e) {
                throw { _network: true, message: 'Errore di connessione' };
            }

            const data = await res.json().catch(() => ({}));

            if (!res.ok) {
                throw data;
            }

            return data;
        },
    });
}
